import { useState, useEffect, useRef } from 'react';
import { Heart, ExternalLink, CheckCircle, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import ShareButton from './ShareButton';
import ActivitySuccessModal from './ActivitySuccessModal';
import RegistrationOTPModal from './RegistrationOTPModal';
import { useAuth } from '../context/AuthContext';
import { track } from '../utils/analytics';

const ONBOARDING_KEY = 'karmana_new_user_onboarding';

export default function ActivityJoinSection({
  activityId,
  activityName,
  activitySlug,
  isPast,
  interested,
  interestLoading,
  onJoin,
  registrationLink,
  registrationClosed,
  registrationFull,
  registrationType,
  registrationFields,
  registrationRequirement,
  registrationEndDate,
  alreadyRegistered,
  onRegisterClick,
  onRegisterButtonClick,
  autoShowSuccess = false,
  onAutoShowSuccessConsumed,
  onRegistered,
  onSignIn,
  inlineOnly = false,
  onScrollToInlineReg,
  // Detail page: parent manages OTP modal externally
  pendingOTPData = null,
  onReopenOTP = null,
  sourceProps = {},
  shareCardContext = {},
  exploreOverride = null,
  activityJourney = false,
  showTutorial = false,
  onTutorialDismiss,
  embedUrl = null,   // Google Forms embed URL — passed to ActivitySuccessModal
}) {
  const { user, setUser, triggerOnboarding } = useAuth();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const formFieldsFilledRef = useRef(0);   // updated via onFieldsChange callback from KarmanaRegistrationForm
  const formTotalFieldsRef  = useRef(0);
  const otpOpenedRef        = useRef(false);  // was OTP modal opened during this session?
  const regSucceededRef     = useRef(false);  // did registration complete successfully?

  const isKarmana = registrationType === 'karmana';

  // ── Session-persistent state ─────────────────────────────────────────────────

  // Detail page only: non-signed-in user clicked "I'm Interested" for Karmana
  const [localInterestClicked, setLocalInterestClickedRaw] = useState(() => {
    if (!inlineOnly || !isKarmana || !activityId) return false;
    try { return sessionStorage.getItem(`kp_int_${activityId}`) === '1'; }
    catch { return false; }
  });

  // Listing page only: form submitted but OTP not yet completed
  // → shows "You're all set. Sign up" CTA
  const [internalPendingOTP, setInternalPendingOTP] = useState(() => {
    if (inlineOnly || !isKarmana || !activityId) return null;
    try {
      const s = sessionStorage.getItem(`kp_otp_${activityId}`);
      return s ? JSON.parse(s) : null;
    } catch { return null; }
  });

  // Effective pending OTP: listing page uses internal state; detail page uses prop from parent
  const effectivePendingOTP = internalPendingOTP || pendingOTPData;

  // Tutorial tooltip: visible only in the default "I'm Interested" state for signed-out users
  const showTutorialTooltip = showTutorial && !isPast && !interested &&
    !(isKarmana && !user && effectivePendingOTP) &&
    !(inlineOnly && isKarmana && alreadyRegistered) &&
    !(inlineOnly && isKarmana && localInterestClicked) &&
    !exploreOverride;

  // Persistent tooltip for expired activity explore section
  const [persistentMsgDismissed, setPersistentMsgDismissed] = useState(false);
  const showPersistentTooltip = !!(exploreOverride?.persistentMsg) && !persistentMsgDismissed;

  // ── Setters with sessionStorage sync ────────────────────────────────────────

  const setLocalInterestClicked = (v) => {
    setLocalInterestClickedRaw(v);
    try {
      if (v) sessionStorage.setItem(`kp_int_${activityId}`, '1');
      else sessionStorage.removeItem(`kp_int_${activityId}`);
    } catch {}
  };

  // ── Clear all ephemeral session state when user signs in ─────────────────────
  useEffect(() => {
    if (user && activityId) {
      setInternalPendingOTP(null);
      setLocalInterestClickedRaw(false);
      try {
        sessionStorage.removeItem(`kp_otp_${activityId}`);
        sessionStorage.removeItem(`kp_int_${activityId}`);
      } catch {}
    }
  }, [user?.id]); // eslint-disable-line

  useEffect(() => {
    if (autoShowSuccess && !inlineOnly) {
      setShowSuccessModal(true);
      onAutoShowSuccessConsumed?.();
    }
  }, [autoShowSuccess]); // eslint-disable-line

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleJoinClick = async () => {
    if (isPast) return;
    // Dismiss tutorial in parallel — normal flow continues below
    if (showTutorialTooltip) onTutorialDismiss?.();

    if (inlineOnly) {
      if (isKarmana && !user) {
        setLocalInterestClicked(true);
      }
      try {
        await onJoin();
      } catch {
        toast.error('Something went wrong. Please try again.');
      }
      return;
    }

    if (!user) {
      if (isKarmana) {
        setShowSuccessModal(true);
        return;
      }
      onJoin();
      return;
    }

    setShowSuccessModal(true);
    try {
      await onJoin();
    } catch {
      setShowSuccessModal(false);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleRegistered = () => {
    regSucceededRef.current = true;
    setShowSuccessModal(false);
    if (onRegistered) onRegistered(activityId);
  };

  const handleSignIn = (newUser) => {
    if (setUser) setUser(newUser);
    if (onSignIn) onSignIn(newUser);
  };

  const handleOTPRequired = (data) => {
    setShowSuccessModal(false);
    otpOpenedRef.current = true;
    const otpInfo = { ...data, activityId };
    setOtpData(otpInfo);
    // Track pending OTP for "You're all set. Sign up" listing page state (GAP 3)
    if (!inlineOnly) {
      setInternalPendingOTP(otpInfo);
      try { sessionStorage.setItem(`kp_otp_${activityId}`, JSON.stringify(otpInfo)); } catch {}
    }
  };

  // Handler for "You're all set. Sign up" click (GAP 4)
  const handleSignUpOTP = () => {
    if (inlineOnly) {
      // Detail page: delegate to parent to reopen OTP modal
      onReopenOTP?.();
    } else {
      // Listing page: reopen OTP modal directly
      setOtpData(internalPendingOTP);
    }
  };

  return (
    <div>
      {/* ── Success modal ─────────────────────────────────────────────── */}
      {showSuccessModal && (
        <ActivitySuccessModal
          activityName={activityName}
          activityId={activityId}
          activitySlug={activitySlug}
          registrationLink={registrationLink}
          registrationEndDate={registrationEndDate}
          isKarmana={isKarmana}
          registrationFull={!!registrationFull}
          registrationFields={registrationFields}
          registrationRequirement={registrationRequirement}
          alreadyRegistered={alreadyRegistered}
          user={user}
          embedUrl={embedUrl}
          onRegisterClick={onRegisterClick}
          onClose={() => {
            if (!regSucceededRef.current) {
              track('karmana_reg_abandoned', {
                activity_id: sourceProps?.activity_id || activityId,
                step: otpOpenedRef.current ? 'otp' : 'form',
                fields_filled: formFieldsFilledRef.current,
                total_fields: formTotalFieldsRef.current,
              });
            }
            setShowSuccessModal(false);
            try {
              if (sessionStorage.getItem(ONBOARDING_KEY)) triggerOnboarding(4000);
            } catch { /* ignore */ }
          }}
          onRegistered={handleRegistered}
          onSignIn={handleSignIn}
          onOTPRequired={handleOTPRequired}
          sourceProps={sourceProps}
          onFieldsChange={(filled, total) => {
            formFieldsFilledRef.current = filled;
            formTotalFieldsRef.current = total;
          }}
        />
      )}

      {/* ── OTP sign-in modal (listing page only — detail page OTP is at page level) ── */}
      {otpData && !inlineOnly && (
        <RegistrationOTPModal
          activityId={otpData.activityId}
          method={otpData.method}
          contact={otpData.email}
          pendingRegId={otpData.pendingRegId}
          email={otpData.email}
          confirmResult={otpData.confirmResult}
          sourceProps={sourceProps}
          onSuccess={(verifiedUser) => {
            if (verifiedUser && setUser) setUser(verifiedUser);
            regSucceededRef.current = true;
            setOtpData(null);
            // Clear pending OTP — user is now signed in (GAP 2 fix)
            setInternalPendingOTP(null);
            try { sessionStorage.removeItem(`kp_otp_${activityId}`); } catch {}
          }}
          onClose={() => {
            // Fire abandonment only — success is handled in onSuccess
            track('karmana_reg_abandoned', {
              activity_id: sourceProps?.activity_id || activityId,
              step: 'otp',
              otp_method: otpData?.method,
            });
            setOtpData(null);
          }}
        />
      )}

      {/* ── Row: separator + join/share ─────────────────────────────── */}
      <div>
        <div className="border-t border-gray-100 mx-4" />

        {/* ── Standard interest/share row ── */}
        <div className="flex items-stretch py-2 relative">
          {/* ── Tutorial: grey transparent overlay dims everything except the CTA button ── */}
          {showTutorialTooltip && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'rgba(156, 163, 175, 0.45)', zIndex: 25 }}
            />
          )}
          <div className="w-[4%]" aria-hidden="true" />

          {/* ── Main CTA ─────────────────────────────────────────────── */}
          <div className="w-[70%] flex">
            {!interested ? (
              /* ── State 1: Not yet interested — with sub-states ── */

              // GAP 3: Form submitted but OTP not completed → "You're all set. Sign up"
              (isKarmana && !user && effectivePendingOTP) ? (
                <button
                  onClick={handleSignUpOTP}
                  data-testid={`signup-otp-btn-${activityId}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 bg-green-600 border-green-600 text-white hover:bg-green-700 transition-colors active:scale-[0.97]"
                >
                  <CheckCircle size={17} stroke="white" />
                  <span className="text-sm font-bold">You&apos;re all set. Sign up</span>
                  <LogIn size={13} className="flex-shrink-0 opacity-80" />
                </button>

              // GAP 5: "Registration Done" renamed to "You're all set" (disabled)
              ) : (inlineOnly && isKarmana && alreadyRegistered) ? (
                <button
                  disabled
                  data-testid={`all-set-inline-btn-${activityId}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 bg-green-600 border-green-600 text-white cursor-default"
                >
                  <CheckCircle size={18} stroke="white" />
                  <span className="text-sm font-bold">You&apos;re all set</span>
                </button>

              ) : (inlineOnly && isKarmana && localInterestClicked) ? (
                /* Detail page: clicked interest, scroll-to-form state */
                <button
                  onClick={() => {
                    track('activity_interested', { action: 'registration_required_scroll', cta_state: 'registration_required_scroll', activity_id: activityId, source_page: sourceProps.source_page || 'activity_detail' });
                    onScrollToInlineReg?.();
                  }}
                  data-testid={`reg-required-inline-btn-${activityId}`}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 bg-green-600 border-green-600 text-white hover:bg-green-700 transition-colors active:scale-[0.97]"
                >
                  <Heart size={18} fill="white" stroke="white" strokeWidth={1.5} />
                  <span className="text-sm font-bold">Registration Required</span>
                </button>

              ) : (
                /* Default: I'm Interested — or explore override */
                exploreOverride ? (
                  /* ── Expired activity: "This Activity is Over" + persistent tooltip ── */
                  <div className="flex-1 relative" style={showPersistentTooltip ? { zIndex: 30 } : undefined}>
                    {showPersistentTooltip && (
                      <>
                        <style>{`@keyframes karmana-tooltip-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}`}</style>
                        <div
                          className="absolute z-50 left-0 right-0"
                          style={{ bottom: 'calc(100% + 4px)', pointerEvents: 'auto' }}
                          data-testid="expired-activity-persistent-tooltip"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPersistentMsgDismissed(true);
                            exploreOverride.onClick?.();
                          }}
                        >
                          <div style={{
                            background: '#1f2937',
                            borderRadius: 10,
                            padding: '13px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
                            animation: 'karmana-tooltip-pulse 2.5s ease-in-out infinite',
                            transformOrigin: 'center bottom',
                            cursor: 'pointer',
                          }}>
                            <span style={{ color: '#f9fafb', fontSize: 13, fontWeight: 600, flex: 1, lineHeight: 1.4 }}>
                              {exploreOverride.persistentMsg}
                            </span>
                            <img
                              src="https://customer-assets-agu9un31.emergentagent.net/job_295b1699-a469-43cc-8441-0f8cadbdd83f/artifacts/jqyghr7b_Log%20out%20state.png"
                              alt=""
                              style={{ width: 14, height: 14, flexShrink: 0, objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.65 }}
                            />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{ width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: '7px solid #1f2937' }} />
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      onClick={exploreOverride.onClick}
                      data-testid={`explore-cta-btn-${activityId}`}
                      className="w-full flex items-center justify-center gap-1.5 py-3 rounded-xl border-2 bg-gray-100 text-gray-500 hover:bg-gray-150 active:scale-[0.97] transition-all"
                      style={{ borderColor: '#e5e7eb' }}
                    >
                      <span className="text-sm font-bold text-center px-1">{exploreOverride.text}</span>
                    </button>
                  </div>
                ) : (
                  /* ── Tutorial-aware wrapper ── */
                  <div className="flex-1 relative" style={showTutorialTooltip ? { zIndex: 30 } : undefined}>
                    {showTutorialTooltip && (
                      <>
                        <style>{`@keyframes karmana-tooltip-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}`}</style>
                        <div
                          className="absolute z-50 left-0 right-0"
                          style={{ bottom: 'calc(100% + 4px)', pointerEvents: 'auto' }}
                          data-testid="activities-tutorial-tooltip"
                        >
                          {/* Tooltip box — dark theme, slow pulse animation */}
                          <div style={{
                            background: '#1f2937',
                            borderRadius: 10,
                            padding: '13px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
                            animation: 'karmana-tooltip-pulse 2.5s ease-in-out infinite',
                            transformOrigin: 'center bottom',
                          }}>
                            <span style={{ color: '#f9fafb', fontSize: 13, fontWeight: 600, flex: 1, lineHeight: 1.4 }}>
                              Click here to Join
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); onTutorialDismiss?.(); }}
                              data-testid="activities-tutorial-close-btn"
                              aria-label="Close tutorial"
                              style={{
                                background: 'none', border: 'none',
                                color: '#9ca3af', fontSize: 17, fontWeight: 700,
                                cursor: 'pointer', padding: '0 2px',
                                lineHeight: 1, flexShrink: 0,
                              }}
                            >
                              ×
                            </button>
                          </div>
                          {/* Down-pointing arrow centered below the tooltip box */}
                          <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{
                              width: 0, height: 0,
                              borderLeft: '7px solid transparent',
                              borderRight: '7px solid transparent',
                              borderTop: '7px solid #1f2937',
                            }} />
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      onClick={handleJoinClick}
                      disabled={interestLoading}
                      data-testid={`join-btn-${activityId}`}
                      className={[
                        'w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition-all',
                        isPast
                          ? 'bg-gray-50 border-gray-200 text-gray-300'
                          : 'bg-green-50 text-green-600 hover:bg-green-100 active:scale-[0.97]',
                        interestLoading ? 'opacity-60' : '',
                      ].join(' ')}
                      style={showTutorialTooltip ? {
                        borderColor: '#374151',
                        boxShadow: '0 0 0 3px rgba(55,65,81,0.15)',
                      } : {
                        borderColor: 'transparent',
                      }}
                    >
                      <span className={!isPast ? 'heart-pulse' : ''}>
                        <Heart size={20} fill="white" stroke="currentColor" strokeWidth={1.5} />
                      </span>
                      <span className="text-sm font-bold">I&apos;m Interested</span>
                    </button>
                  </div>
                )
              )

            ) : (isKarmana && !alreadyRegistered) || (registrationLink && !registrationClosed) ? (
              /* ── State 2: Interested — registration still required ── */
              <button
                onClick={() => {
                  track('activity_interested', { action: 'registration_required', cta_state: 'registration_required', activity_id: activityId, source_page: sourceProps.source_page || 'unknown' });
                  if (inlineOnly) {
                    onScrollToInlineReg?.();
                  } else {
                    onRegisterButtonClick?.();
                    setShowSuccessModal(true);
                  }
                }}
                data-testid={`register-required-btn-${activityId}`}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 bg-green-600 border-green-600 text-white hover:bg-green-700 transition-colors active:scale-[0.97]"
              >
                <Heart size={18} fill="white" stroke="white" strokeWidth={1.5} />
                <span className="text-sm font-bold">Registration Required</span>
                {registrationLink && !isKarmana && !embedUrl && <ExternalLink size={13} className="flex-shrink-0" />}
              </button>

            ) : (
              /* ── State 3: All set (no registration, or Karmana registration done) ── */
              <button
                onClick={() => {
                  if (isPast) {
                    track('activity_interested', { action: 'attended', cta_state: 'attended', activity_id: activityId, source_page: sourceProps.source_page || 'unknown' });
                  } else if (activityJourney) {
                    track('activity_interested', { action: 'journey_tap', cta_state: 'youre_all_set', activity_id: activityId, source_page: sourceProps.source_page || 'unknown' });
                    if (inlineOnly) {
                      onJoin(); // detail page: scrolls to Activity Journey section
                    } else {
                      window.location.href = `/activities/${activitySlug}?section=journey`;
                    }
                  } else if (!inlineOnly) {
                    track('activity_interested', { action: 'youre_all_set', cta_state: 'youre_all_set', activity_id: activityId, source_page: sourceProps.source_page || 'unknown' });
                    setShowSuccessModal(true);
                  }
                }}
                data-testid={`all-set-btn-${activityId}`}
                className={[
                  'flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 bg-green-600 border-green-600 text-white',
                  (!isPast && (activityJourney || !inlineOnly)) ? 'hover:bg-green-700 transition-colors active:scale-[0.97]' : 'cursor-default',
                ].join(' ')}
              >
                <Heart size={18} fill="white" stroke="white" strokeWidth={1.5} />
                <span className="text-sm font-bold">
                  {isPast ? 'Attended!' : "You're Interested"}
                </span>
              </button>
            )}
          </div>

          <div className="flex-1 ml-1.5" onClick={() => { if (showTutorialTooltip) onTutorialDismiss?.(); }}>
            <ShareButton
              activityId={activityId}
              activityName={activityName}
              activitySlug={activitySlug}
              variant="activity-share"
              cardContext={shareCardContext}
            />
          </div>
        </div>

        {/* ── Inline confirmation removed — Journey section handles feedback ── */}
      </div>
    </div>
  );
}
